import * as React from 'react';
import {
  Box,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { isValidPhoneNumber } from "libphonenumber-js";
import tokens from "../../theme";

const { colors, fontFamily } = tokens;

export const emptyEmployee = () => ({
  email: "",
  employee_id: "",
  first_name: "",
  last_name: "",
  gender: "M",
  withdrawal_percent: "0.00",
  profile: {
    position: "",
    phone: "",
    salary: "",
    savings: "",
    nin: "",
    bvn: "",
  },
  bank: {
    name: "",
    code: "",
    number: "",
    iban: "",
    account_holder: "",
  },
  branch: "",
});

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    color: colors.Violet,
    fontFamily: fontFamily.MARKPROMEDIUM,
  },
  divider: {
    backgroundColor: colors.Gallery,
    marginBottom: theme.spacing(2),
  },
  field: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.White,
      fontFamily: fontFamily.MARKPROBOOK,
      "& fieldset": { borderColor: colors.Periwinkle },
      "&:hover fieldset": { borderColor: colors.DodgerBlue },
      "&.Mui-focused fieldset": {
        borderColor: colors.DodgerBlue,
        borderWidth: 1.5,
      },
    },
    "& .MuiOutlinedInput-input": { color: colors.Violet },
    "& .MuiInputLabel-root": {
      color: "#5A5A7A",
      fontFamily: fontFamily.MARKPRO,
      "&.Mui-focused": { color: colors.DodgerBlue },
    },
  },
}));

export default function EmployeeForm({ value, onChange, loading = false, disabled = false, setIsValid = null }) {
  const classes = useStyles();

  React.useEffect(() => {
    if (setIsValid) {
      const isValid = value.first_name.trim() !== "" &&
        value.last_name.trim() !== "" &&
        value.employee_id.trim() !== "" &&
        value.email.trim() !== "" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email) &&
        value.profile.position.trim() !== "" &&
        value.branch.trim() !== "" &&
        value.profile.salary !== "" && !isNaN(value.profile.salary) && Number(value.profile.salary) >= 0 &&
        value.profile.savings !== "" && !isNaN(value.profile.savings) && Number(value.profile.savings) >= 0 &&
        value.bank.account_holder.trim() !== "" &&
        value.bank.name.trim() !== "" &&
        value.bank.code.trim() !== "" &&
        value.bank.number.trim() !== "" && /^\d+$/.test(value.bank.number) && value.bank.number.length === 10 &&
        (value.profile.phone.trim() === "" || isValidPhoneNumber(value.profile.phone, "NG")) &&
        (value.profile.nin.trim() === "" || (value.profile.nin.length === 11 && /^\d+$/.test(value.profile.nin))) &&
        (value.profile.bvn.trim() === "" || (value.profile.bvn.length === 11 && /^\d+$/.test(value.profile.bvn)));
      setIsValid(isValid);
    }
  }, [value, setIsValid]);

  const setField = (path) => (e, val = null) => {
    const v = val !== null ? val : e.target.value;
    const next = { ...value };
    if (path.length === 1) next[path[0]] = v;
    else next[path[0]] = { ...value[path[0]], [path[1]]: v };
    onChange(next);
  };

  const field = (label, path, extra = {
  }) => (
    <Grid item xs={12} sm={6}>
      <TextField
        className={classes.field}
        variant="outlined"
        fullWidth
        label={label}
        value={path.length === 1 ? value[path[0]] : value[path[0]][path[1]]}
        onChange={setField(path)}
        disabled={disabled || loading}
        {...extra}
      />
    </Grid>
  );

  return (
    <Box>
      <Typography variant="subtitle1" className={classes.sectionTitle}>
        Personal
      </Typography>
      <Divider className={classes.divider} />
      <Grid container spacing={2}>
        {field("First Name", ["first_name"], { required: true })}
        {field("Last Name", ["last_name"], { required: true })}
        <Grid item xs={12} sm={6}>
          <TextField
            className={classes.field}
            variant="outlined"
            fullWidth
            select
            required
            label="Gender"
            value={value.gender}
            onChange={(e) => {
              setField(["gender"])(e)
            }}
          >
            <MenuItem value="M">Male</MenuItem>
            <MenuItem value="F">Female</MenuItem>
          </TextField>
        </Grid>
        {field("Employee ID", ["employee_id"], {
          required: true, onChange: function (e) {
            const val = e.target.value;
            if (/^[a-zA-Z0-9_-]*$/.test(val)) setField(["employee_id"], val?.toUpperCase())(e);
          }
        })}
        {field("Email", ["email"], {
          required: true,
          type: "email",
          error: value.email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email),
          helperText: value.email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email) ? "Enter a valid email address" : ""
        })}
        {field("Phone number", ["profile", "phone"], {
          required: true,
          type: "tel",
          onChange: function (e) {
            const phone = e.target.value;
            if (phone === "" || /^\d*$/.test(phone)) setField(["profile", "phone"])(e);
          },
          error: function () {
            const phone = value.profile.phone;
            if (phone === "") return false;
            if (!phone) return false;
            if (!isValidPhoneNumber(phone, "NG")) return true;
            return false;
          }(),
          helperText: function () {
            const phone = value.profile.phone;
            if (phone === "") return "";
            if (!phone) return "";
            if (!isValidPhoneNumber(phone, "NG")) return "Enter a valid Nigerian phone number";
            return "";
          }()
        })}

      </Grid>

      <Typography variant="subtitle1" className={classes.sectionTitle}>
        Employment
      </Typography>
      <Divider className={classes.divider} />
      <Grid container spacing={2}>
        {field("Title", ["profile", "position"], { required: true })}
        {field("Office Location", ["branch"], { required: true })}
        {field("Full Net Salary", ["profile", "salary"], {
          required: true,
          // type: "number",
          onChange: function (e) {
            const salary = e.target.value;
            if (salary === "" || /^\d*$/.test(salary)) setField(["profile", "salary"])(e);
          },
          InputProps: {
            startAdornment: <span
              style={{ color: colors.Violet, marginRight: 4, fontFamily: fontFamily.MARKPROBOLD }}
            >₦</span>
          },
          min: 0,
        })}
        {field("Savings", ["profile", "savings"], {
          required: true,
          // type: "number",
          onChange: function (e) {
            const savings = e.target.value;
            console.log(savings);
            if (savings === "" || /^\d*$/.test(savings)) setField(["profile", "savings"])(e);
          },
          InputProps: {
            startAdornment: <span style={{ color: colors.Violet, marginRight: 4, fontFamily: fontFamily.MARKPROBOLD }}>₦</span>
          },
          min: 0,
        })}
      </Grid>

      <Typography variant="subtitle1" className={classes.sectionTitle}>
        Bank
      </Typography>
      <Divider className={classes.divider} />
      <Grid container spacing={2}>
        {field("Account holder name", ["bank", "account_holder"], { required: true })}
        {field("Bank Name", ["bank", "name"], { required: true })}
        {field("Bank Code", ["bank", "code"], { required: true })}
        {field("Account number", ["bank", "number"], {
          onChange: function (e) {
            const number = e.target.value;
            if (number === "" || /^\d*$/.test(number)) setField(["bank", "number"])(e);
          },
          required: true,
          error: function () {
            const number = value.bank.number;
            if (number === "") return false;
            if (!number) return false;
            if (number.length < 10) return true;
            if (!/^\d+$/.test(number)) return true;
            return false;
          }(),
          helperText: function () {
            const number = value.bank.number;
            if (number === "") return "";
            if (!number) return "";
            if (number.length !== 10) return "Account number must be between 10 digits";
            if (!/^\d+$/.test(number)) return "Account number must contain only digits";
            return "";
          }()
        }
        )
        }
        {field("IBAN (optional)", ["bank", "iban"])}
      </Grid>

      <Typography variant="subtitle1" className={classes.sectionTitle}>
        Identification
      </Typography>
      <Divider className={classes.divider} />
      <Grid container spacing={2}>
        {field("NIN", ["profile", "nin"], {
          required: false, error: function () {
            const nin = value.profile.nin;
            if (nin === "") return false;
            if (!nin) return false;
            if (nin.length !== 11) return true;
            if (!/^\d+$/.test(nin)) return true;
            return false;
          }(),
          helperText: function () {
            const nin = value.profile.nin;
            if (nin === "") return "";
            if (!nin) return "";
            if (nin.length !== 11) return "NIN must be 11 digits";
            if (!/^\d+$/.test(nin)) return "NIN must contain only digits";
            return "";
          }()
        }
        )}
        {field("BVN", ["profile", "bvn"], {
          required: false,
          error: function () {
            const bvn = value.profile.bvn;
            if (bvn === "") return false;
            if (!bvn) return false;
            if (bvn.length !== 11) return true;
            if (!/^\d+$/.test(bvn)) return true;
            return false;
          }(),
          helperText: function () {
            const bvn = value.profile.bvn;
            if (bvn === "") return "";
            if (!bvn) return "";
            if (bvn.length !== 11) return "BVN must be 11 digits";
            if (!/^\d+$/.test(bvn)) return "BVN must contain only digits";
            return "";
          }

        })}
      </Grid>
    </Box>
  );
}
